import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Autocomplete from '@mui/material/Autocomplete';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditIcon from '@mui/icons-material/Edit';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Alert from '@mui/material/Alert';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import MuiTooltip from '@mui/material/Tooltip';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { makeApiRequest } from '../../../utils/api';
import { styled } from '@mui/material/styles';
import debounce from 'lodash/debounce';
import { tableCellClasses } from '@mui/material/TableCell';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import DoughnutChart from './DoughnutChart';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: '#F1F4F9',
        color: '#333',
        fontWeight: 600,
        padding: '8px',
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: '0.875rem',
        padding: '8px',
        borderBottom: '1px solid #EAEAEA',
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:hover': {
        backgroundColor: '#f5f7fa',
    },
    '&.Mui-selected': {
        backgroundColor: '#B9F4FF',
        '&:hover': {
            backgroundColor: '#B9F4FF',
        },
    },
}));

function StudentAssessmentEntryForm() {
    const navigate = useNavigate();
    const control = localStorage.getItem('control');
    const userLocation = localStorage.getItem('userLocation');
    const [batchValue, setBatchValue] = useState('');
    const [assessmentValue, setAssessmentValue] = useState('');
    const [selectedTab, setSelectedTab] = useState(0);
    const [written, setWritten] = useState(0);
    const [viva, setViva] = useState(0);
    const [writtenInput, setWrittenInput] = useState('');
    const [vivaInput, setVivaInput] = useState('');
    const [markInputs, setMarkInputs] = useState({});
    const [markErrors, setMarkErrors] = useState({});
    const [writtenError, setWrittenError] = useState(false);
    const [vivaError, setVivaError] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [studentsList, setStudentsList] = useState([]);
    const [pendingAssessments, setPendingAssessments] = useState([]);
    const [completedAssessments, setCompletedAssessments] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [batchOptions, setBatchOptions] = useState([]);
    const [assessmentOptions, setAssessmentOptions] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [courseInfo, setCourseInfo] = useState(null);
    const [subjectName, setSubjectName] = useState('');
    const [selectedAssessmentName, setSelectedAssessmentName] = useState('');
    const [activeSearchTerm, setActiveSearchTerm] = useState('');
    const [isAssessmentCompleted, setIsAssessmentCompleted] = useState(false);
    const [activeAssessmentValue, setActiveAssessmentValue] = useState('');
    const [submittedFields, setSubmittedFields] = useState({});
    const [selectedLocation, setSelectedLocation] = useState(
        (control == 4) ? Number(userLocation) : 0
    );
    const [allBatchOptions, setAllBatchOptions] = useState([]);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editSpecificNameInput, setEditSpecificNameInput] = useState('');
    const [isSavingName, setIsSavingName] = useState(false);
    const [isConfirmNameDialogOpen, setIsConfirmNameDialogOpen] = useState(false);

    const scoreOptions = Array.from({ length: 6 }, (_, i) => i);
    const selectedBatch = useMemo(
        () =>
            allBatchOptions.find(
                (batch) => String(batch.batch_id) === String(batchValue)
            ) || null,
        [allBatchOptions, batchValue]
    );

    useEffect(() => {
        fetchBatches();
    }, []);

    useEffect(() => {
        if (batchValue) {
            fetchAssessments();
        } else {
            setAssessmentOptions([]);
            setAssessmentValue('');
        }
    }, [batchValue]);

    const fetchBatches = async () => {
        try {
            const response = await makeApiRequest('post', '/api/listBatches/filter', {
                token: localStorage.getItem('authToken'),
            });

            if (response && response.data) {
                setAllBatchOptions(response.data);
                setBatchOptions(response.data);
            } else {
                throw new Error('Invalid response from batch API');
            }
        } catch (err) {
            console.error('Error fetching batches:', err);
            setError('Failed to load batches. Please try again.');
        }
    };
    useEffect(() => {
        if (selectedLocation === 0) {
            setBatchOptions(allBatchOptions);
        } else {
            const filtered = allBatchOptions.filter(
                (batch) => Number(batch.location) === Number(selectedLocation)
            );
            setBatchOptions(filtered);
        }
    }, [selectedLocation, allBatchOptions]);


    const fetchAssessments = async () => {
        if (!batchValue) {
            setAssessmentOptions([]);
            return;
        }

        try {
            const courseId = selectedBatch ? selectedBatch.course_id : null;

            if (!courseId) {
                throw new Error('Course ID not found for selected batch');
            }

            const response = await makeApiRequest('post', '/api/assessments', {
                token: localStorage.getItem('authToken'),
                course_id: courseId,
                batch_id: batchValue,
            });

            if (response && response.status && response.data) {
                if (response.data.length === 0) {
                    setAssessmentOptions([]);
                    setError('Assessments are not configured for this course. please contact admin.');
                    return;
                }
                const formattedAssessments = response.data.map((assessmentName, index) => ({
                    id: index,
                    assesment_name: assessmentName,
                }));
                setAssessmentOptions(formattedAssessments);
            } else {
                throw new Error('Invalid response from assessments API');
            }
        } catch (err) {
            console.error('Error fetching assessments:', err);
            setError('Assessments are not configured for this course. please contact admin.');
            setAssessmentOptions([]);
        }
    };

    const fetchStudents = async () => {
        if (!batchValue || !assessmentValue) {
            setError('Please select both batch and assessment');
            return;
        }

        setSelectedAssessmentName(assessmentValue);
        setActiveAssessmentValue(assessmentValue);

        if (!selectedBatch) {
            setError('Selected batch not found');
            return;
        }

        const batchCourseKey = `${selectedBatch.batch_id}_${selectedBatch.course_id}`;

        setLoading(true);
        setError(null);
        setSuccess(null);
        setStudentsList([]);
        setSelectedStudent(null);
        setSelectedRow(null);
        setWrittenInput('');
        setVivaInput('');
        setWritten(0);
        setViva(0);
        setWrittenError(false);
        setVivaError(false);
        setSubmittedFields({});

        try {
            const response = await makeApiRequest('post', '/api/listStudents', {
                token: localStorage.getItem('authToken'),
                batch_course_key: batchCourseKey,
                assessment_name: assessmentValue,
            });

            const students = response?.data ?? [];

            if (!Array.isArray(students)) {
                throw new Error('Invalid student data');
            }

            // Sort students: pending first, completed last
            const sortedStudents = [...students].sort((a, b) => {
                const statusA =
                    a.assessments?.find((a) => a.generic_name === assessmentValue)?.status || 'pending';
                const statusB =
                    b.assessments?.find((a) => a.generic_name === assessmentValue)?.status || 'pending';
                if (statusA === 'pending' && statusB === 'completed') return -1;
                if (statusA === 'completed' && statusB === 'pending') return 1;
                return 0;
            });

            setStudentsList(sortedStudents);
            setActiveSearchTerm(searchTerm);

            const newSubmittedFields = {};
            sortedStudents.forEach((student) => {
                const selectedAssessment =
                    student.assessments?.find((a) => a.generic_name === assessmentValue) || {
                        marks: {},
                        status: 'pending',
                    };
                const studentMarks = selectedAssessment.marks || {};
                const isCompleted = selectedAssessment.status === 'completed';

                const fieldsSubmitted = {};
                Object.entries(studentMarks).forEach(([field, value]) => {
                    if (field !== 'total' && value !== null && value !== '' && value !== undefined) {
                        fieldsSubmitted[field] = true;
                    }
                });

                if (isCompleted) {
                    Object.keys(studentMarks).forEach((field) => {
                        if (field !== 'total') {
                            fieldsSubmitted[field] = true;
                        }
                    });
                }

                const studentAssessmentKey = `${student.student_id}_${assessmentValue}`;
                if (Object.keys(fieldsSubmitted).length > 0) {
                    newSubmittedFields[studentAssessmentKey] = fieldsSubmitted;
                }
            });

            setSubmittedFields(newSubmittedFields);

            const assessmentMap = new Map();
            sortedStudents.forEach((student) => {
                (student.assessments || []).forEach((a) => {
                    const key = a.assessment_id;
                    if (!assessmentMap.has(key)) {
                        assessmentMap.set(key, {
                            assessment_id: a.assessment_id,
                            assessment_name: a.generic_name.toLowerCase().trim(),
                            status: a.status,
                            marks: a.marks,
                            specific_name: a.specific_name,
                        });
                    } else {
                        const existing = assessmentMap.get(key);
                        if (existing.status !== 'completed' && a.status === 'completed') {
                            existing.status = 'completed';
                        }
                    }
                });
            });

            const allAssessments = Array.from(assessmentMap.values());
            const pending = allAssessments.filter((a) => a.status === 'pending');
            const completed = allAssessments.filter((a) => a.status === 'completed');

            setPendingAssessments(pending);
            setCompletedAssessments(completed);

            setCourseInfo({
                batchName: selectedBatch.batch_name,
                courseName: sortedStudents[0]?.course_name || 'Course Information',
            });

            if (sortedStudents.length === 0) {
                setError('No students found.');
                setSelectedStudent(null);
                setSelectedRow(null);
            }
        } catch (err) {
            console.error('Error fetching students:', err);
            setError('Failed to load students. Please try again.');
            setStudentsList([]);
        } finally {
            setLoading(false);
        }
    };

    const handleStudentSelect = (index) => {
        const student = studentsList[index];

        if (!student) {
            // setError('Selected student not found');
            setSelectedStudent(null);
            setSelectedRow(null);
            setPendingAssessments([]);
            setCompletedAssessments([]);
            setMarkInputs({});
            setMarkErrors({});
            setWrittenInput('');
            setVivaInput('');
            setWritten(0);
            setViva(0);
            setWrittenError(false);
            setVivaError(false);
            setSubjectName('');
            return;
        }

        // Ensure assessments exists, default to empty array if undefined
        const assessments = student.assessments || [];
        const selectedAssessment =
            assessments.find((a) => a.generic_name === activeAssessmentValue) || {
                marks: {},
                status: 'pending',
                specific_name: '',
            };

        const isCompleted = selectedAssessment.status === 'completed';
        setIsAssessmentCompleted(isCompleted);

        const subjectName = selectedAssessment.specific_name || 'N/A';
        setSubjectName(subjectName);

        const pendingAssmts = assessments.filter((a) => a.status === 'pending');
        const completedAssmts = assessments.filter((a) => a.status === 'completed');

        setPendingAssessments(pendingAssmts);
        setCompletedAssessments(completedAssmts);

        setSelectedStudent({
            ...student,
            subject_name: subjectName,
        });

        const studentMarks = selectedAssessment.marks || {};
        const filteredMarks = {};
        const errors = {};

        const studentAssessmentKey = `${student.student_id}_${activeAssessmentValue}`;
        const existingSubmittedFields = submittedFields[studentAssessmentKey] || {};

        Object.entries(studentMarks).forEach(([field, value]) => {
            if (field !== 'total') {
                filteredMarks[field] = value?.toString() ?? '';
                errors[field] = false;
                if (
                    (value !== null && value !== '' && value !== undefined) ||
                    isCompleted
                ) {
                    existingSubmittedFields[field] = true;
                }
            }
        });

        setMarkInputs(filteredMarks);
        setMarkErrors(errors);
        setWrittenInput(studentMarks?.written?.toString() || '');
        setVivaInput(studentMarks?.viva?.toString() || '');
        setWritten(Number(studentMarks?.written) || 0);
        setViva(Number(studentMarks?.viva) || 0);
        setWrittenError(false);
        setVivaError(false);
        setSubmittedFields((prev) => ({
            ...prev,
            [studentAssessmentKey]: existingSubmittedFields,
        }));

        let displayedStudents = activeSearchTerm
            ? studentsList.filter(
                (s) =>
                    s.student_name?.toLowerCase().includes(
                        activeSearchTerm.toLowerCase()
                    ) ||
                    s.student_id?.toString().includes(activeSearchTerm) ||
                    s.userid?.toString().toLowerCase().includes(activeSearchTerm.toLowerCase())
            )
            : studentsList;

        displayedStudents = [...displayedStudents].sort((a, b) => {
            const statusA =
                a.assessments?.find((a) => a.generic_name === activeAssessmentValue)
                    ?.status || 'pending';
            const statusB =
                b.assessments?.find((a) => a.generic_name === activeAssessmentValue)
                    ?.status || 'pending';
            if (statusA === 'pending' && statusB === 'completed') return -1;
            if (statusA === 'completed' && statusB === 'pending') return 1;
            return 0;
        });

        const displayIndex = displayedStudents.findIndex(
            (s) => s.student_id === student.student_id
        );
        const newPage = Math.floor(displayIndex / rowsPerPage);
        setPage(newPage);
        setSelectedRow(index);

        if (isCompleted) {
            setSuccess(
                "This student's assessment has already been completed. Mark fields are locked."
            );
        } else {
            setSuccess(null);
            setError(null);
        }
    };

    const handleEditSpecificNameClick = () => {
        setEditSpecificNameInput(subjectName);
        setIsEditDialogOpen(true);
    };

    const handleSaveSpecificNameClick = () => {
        if (!editSpecificNameInput.trim()) {
            toast.error('Specific name cannot be empty');
            return;
        }
        setIsConfirmNameDialogOpen(true);
    };

    const handleSaveSpecificName = async () => {
        setIsConfirmNameDialogOpen(false);

        const selectedAssessment = selectedStudent?.assessments?.find(
            (a) => a.generic_name === activeAssessmentValue
        );
        const assessmentId = selectedAssessment?.assessment_id;

        if (!assessmentId) {
            toast.error('Assessment ID not found');
            return;
        }

        setIsSavingName(true);
        try {
            const response = await makeApiRequest('post', '/api/assessments/update-specific-name', {
                token: localStorage.getItem('authToken'),
                assessment_id: assessmentId,
                specific_name: editSpecificNameInput.trim(),
            });

            if (response && response.status === 'success') {
                const newName = editSpecificNameInput.trim();
                
                // Update local states
                setSubjectName(newName);
                
                setStudentsList((prevList) =>
                    prevList.map((student) => ({
                        ...student,
                        assessments: student.assessments?.map((a) =>
                            a.generic_name === activeAssessmentValue
                                ? { ...a, specific_name: newName }
                                : a
                        ),
                    }))
                );

                setSelectedStudent((prevStudent) => {
                    if (!prevStudent) return null;
                    return {
                        ...prevStudent,
                        subject_name: newName,
                        assessments: prevStudent.assessments?.map((a) =>
                            a.generic_name === activeAssessmentValue
                                ? { ...a, specific_name: newName }
                                : a
                        ),
                    };
                });

                setPendingAssessments((prev) =>
                    prev.map((a) =>
                        a.generic_name === activeAssessmentValue
                            ? { ...a, specific_name: newName }
                            : a
                    )
                );

                setCompletedAssessments((prev) =>
                    prev.map((a) =>
                        a.generic_name === activeAssessmentValue
                            ? { ...a, specific_name: newName }
                            : a
                    )
                );

                toast.success('Specific name updated successfully');
                setIsEditDialogOpen(false);
            } else {
                toast.error(response?.message || 'Failed to update specific name');
            }
        } catch (err) {
            console.error('Error saving specific name:', err);
            toast.error(err.response?.data?.message || 'Failed to update specific name');
        } finally {
            setIsSavingName(false);
        }
    };

    useEffect(() => {
        if (studentsList.length > 0 && !selectedStudent && !loading) {
            const displayedStudents = searchTerm
                ? studentsList.filter(
                    (s) =>
                        s.student_name?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
                        s.student_id?.toString()?.includes(searchTerm) ||
                        s.userid?.toString()?.toLowerCase()?.includes(searchTerm.toLowerCase())
                )
                : studentsList;

            const paginatedStudents = displayedStudents.slice(0, rowsPerPage);
            if (paginatedStudents.length > 0) {
                const firstStudent = paginatedStudents[0];
                const firstStudentIndex = studentsList.findIndex(
                    (s) => s.student_id === firstStudent.student_id
                );
                if (firstStudentIndex !== -1) {
                    setPage(0);
                    handleStudentSelect(firstStudentIndex);
                } else {
                    setError('Selected student not found in the student list.');
                    setSelectedStudent(null);
                    setSelectedRow(null);
                }
            } else {
                setError('No students match the search criteria.');
                setSelectedStudent(null);
                setSelectedRow(null);
            }
        }
    }, [studentsList, searchTerm, rowsPerPage, handleStudentSelect, loading]);

    const debouncedSearch = useCallback(
        debounce(() => {
            setActiveSearchTerm(searchTerm);
            setPage(0);
            setSelectedRow(null);
            setSelectedStudent(null);
            setError(null);
            setSuccess(null);
            setMarkInputs({});
            setMarkErrors({});
            setIsAssessmentCompleted(false);
            setSubmittedFields({});

            if (batchValue && assessmentValue) {
                fetchStudents();
            } else {
                setError('Please select both batch and assessment');
            }
        }, 500),
        [searchTerm, batchValue, assessmentValue]
    );

    const handleSearchClick = () => {
        if (!batchValue || !assessmentValue) {
            setError('Please select both batch and assessment');
            return;
        }
        setPage(0); // Ensure the table starts at the first page
        setError(null);
        setSuccess(null);
        fetchStudents();
    };

    const resetFormFields = () => {
        setAssessmentValue('');
        setSearchTerm('');
        setStudentsList([]);
        setSelectedStudent(null);
        setSelectedRow(null);
        setWrittenInput('');
        setVivaInput('');
        setWritten(0);
        setViva(0);
        setWrittenError(false);
        setVivaError(false);
        setPendingAssessments([]);
        setCompletedAssessments([]);
        setCourseInfo(null);
        setError(null);
        setSuccess(null);
        setPage(0);
        setActiveSearchTerm('');
        setSubmittedFields({});
    };

    const handleBatchChange = (event, newValue) => {
        const newBatchValue = newValue ? newValue.batch_id : '';
        resetFormFields();
        setBatchValue(newBatchValue);
    };

    const handleAssessmentChange = (event, newValue) => {
        setAssessmentValue(newValue ? newValue.assesment_name : '');
    };

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };

    const handleSubmitMarks = async () => {
        if (!selectedStudent) {
            setError('Please select a student first');
            return;
        }

        const hasErrors = Object.values(markErrors).some((error) => error === true);
        if (hasErrors) {
            setError('Please fix the errors in your input');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        const individualMarks = Object.fromEntries(
            Object.entries(markInputs).map(([field, val]) => [
                field,
                val === '' ? null : Number(val),
            ])
        );

        const allFieldsFilled = Object.values(individualMarks).every((val) => val !== null);

        const total = allFieldsFilled
            ? Object.values(individualMarks).reduce((sum, val) => sum + Number(val), 0)
            : null;

        try {
            const payload = {
                token: localStorage.getItem('authToken'),
                student_id: selectedStudent.student_id,
                assessment_name: activeAssessmentValue,
                marks: {
                    ...individualMarks,
                    total: total, // only added if all marks are filled
                },
            };

            const allFieldsFilled = Object.values(payload.marks).every((v) => v !== null);

            const studentAssessmentKey = `${selectedStudent.student_id}_${activeAssessmentValue}`;
            const newSubmittedFields = { ...submittedFields[studentAssessmentKey] };
            Object.entries(payload.marks).forEach(([field, value]) => {
                if (value !== null && value !== '') {
                    newSubmittedFields[field] = true;
                }
            });

            setSubmittedFields((prev) => ({
                ...prev,
                [studentAssessmentKey]: newSubmittedFields,
            }));

            const updatedStudents = [...studentsList];
            const updatedStudent = { ...selectedStudent };
            const assessmentToUpdate = updatedStudent.assessments.find(
                (a) => a.generic_name === activeAssessmentValue
            );

            if (assessmentToUpdate) {
                assessmentToUpdate.marks = payload.marks;
                assessmentToUpdate.status = allFieldsFilled ? 'completed' : 'pending';
            }

            updatedStudents[selectedRow] = updatedStudent;
            setStudentsList(updatedStudents);
            setSelectedStudent(updatedStudent);

            const currentAssessment = { ...assessmentToUpdate };

            if (allFieldsFilled) {
                setPendingAssessments((prev) =>
                    prev.filter((item) => item.assessment_id !== currentAssessment.assessment_id)
                );
                setCompletedAssessments((prev) => [...prev, currentAssessment]);
                setIsAssessmentCompleted(true);
            }

            const response = await makeApiRequest('post', '/api/updateMarks', payload);

            if (response && response.message) {
                setSuccess(response.message);
            } else {
                throw new Error('Invalid response from API');
            }
        } catch (err) {
            console.error('Error updating marks:', err);
            const errorMessage = err.response?.data?.message || 'Failed to update marks. Please try again.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitAndContinue = async () => {
        if (!selectedStudent) {
            setError('Please select a student first');
            return;
        }

        if (isAssessmentCompleted) {
            moveToNextStudent();
            return;
        }

        await handleSubmitMarks();

        if (!error && !loading) {
            moveToNextStudent();
        }
    };

    const moveToNextStudent = () => {
        setMarkInputs({});
        setMarkErrors({});
        setError(null);
        setSuccess(null);

        let displayedStudents = activeSearchTerm
            ? studentsList.filter(
                (student) =>
                    student.student_name.toLowerCase().includes(activeSearchTerm.toLowerCase()) ||
                    student.student_id?.toString().includes(activeSearchTerm) ||
                    student.userid?.toString().toLowerCase().includes(activeSearchTerm.toLowerCase())
            )
            : studentsList;

        displayedStudents = [...displayedStudents].sort((a, b) => {
            const statusA =
                a.assessments?.find((a) => a.generic_name === activeAssessmentValue)?.status || 'pending';
            const statusB =
                b.assessments?.find((b) => b.generic_name === activeAssessmentValue)?.status || 'pending';
            if (statusA === 'pending' && statusB === 'completed') return -1;
            if (statusA === 'completed' && statusB === 'pending') return 1;
            return 0;
        });

        const currentPageStudents = displayedStudents.slice(
            page * rowsPerPage,
            (page + 1) * rowsPerPage
        );

        const currentStudent = studentsList[selectedRow];
        const currentIndexInPage = currentPageStudents.findIndex(
            (s) => s.student_id === currentStudent.student_id
        );
        const nextPendingOnPage = currentPageStudents.slice(currentIndexInPage + 1).find(
            (s) =>
                s.assessments?.find((a) => a.generic_name === activeAssessmentValue)?.status === 'pending'
        );

        if (nextPendingOnPage) {
            const nextIndex = studentsList.findIndex((s) => s.student_id === nextPendingOnPage.student_id);
            handleStudentSelect(nextIndex);
        } else {
            const currentDisplayIndex = displayedStudents.findIndex(
                (s) => s.student_id === currentStudent.student_id
            );
            const nextDisplayIndex = currentDisplayIndex + 1;

            if (nextDisplayIndex < displayedStudents.length) {
                const nextStudent = displayedStudents[nextDisplayIndex];
                const nextIndex = studentsList.findIndex((s) => s.student_id === nextStudent.student_id);
                const newPage = Math.floor(nextDisplayIndex / rowsPerPage);
                setPage(newPage);
                handleStudentSelect(nextIndex);
            } else {
                const firstStudent = displayedStudents[0];
                const firstIndex = studentsList.findIndex((s) => s.student_id === firstStudent.student_id);
                setPage(0);
                handleStudentSelect(firstIndex);
                setSuccess('Reached the end of the student list. Starting from the beginning.');
            }
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const displayedStudents = useMemo(() => {
        let result = activeSearchTerm
            ? studentsList.filter(
                (student) =>
                    student.student_name.toLowerCase().includes(activeSearchTerm.toLowerCase()) ||
                    student.student_id?.toString().includes(activeSearchTerm) ||
                    student.userid?.toString().toLowerCase().includes(activeSearchTerm.toLowerCase())
            )
            : studentsList;

        return [...result].sort((a, b) => {
            const statusA =
                a.assessments?.find((a) => a.generic_name === activeAssessmentValue)?.status || 'pending';
            const statusB =
                b.assessments?.find((a) => a.generic_name === activeAssessmentValue)?.status || 'pending';
            if (statusA === 'pending' && statusB === 'completed') return -1;
            if (statusA === 'completed' && statusB === 'pending') return 1;
            return 0;
        });
    }, [studentsList, activeSearchTerm, activeAssessmentValue]);

    const paginatedStudents = displayedStudents.slice(
        page * rowsPerPage,
        (page + 1) * rowsPerPage
    );

    const allStudentsCompleted = () => {
        return studentsList.every((student) =>
            student.assessments?.find((a) => a.generic_name === activeAssessmentValue)?.status === 'completed'
        );
    };

    const maxMarksMap = {
        'modeling': {
            written: 3,
            viva: 3,
            project_presentation: 4,
        },
        'drafting': {
            written: 3,
            viva: 3,
            project_presentation: 4,
        },
        'domain technical concept': {
            written: 5,
            viva: 5,
        },
        'project (drafting + modeling)': {
            project_understanding: 3,
            explanation_technical: 3,
            tools_skill: 4,
        },
        'attendance': {
            attendance: 10,
        },
    };

    const getMaxMark = (field, assessmentTypeOverride = null) => {
        const assessmentType = (
            assessmentTypeOverride || activeAssessmentValue || ''
        ).toLowerCase().trim();

        if (maxMarksMap[assessmentType]?.[field] !== undefined) {
            return maxMarksMap[assessmentType][field];
        }

        const underscoredField = field.replace(/ /g, '_');
        if (maxMarksMap[assessmentType]?.[underscoredField] !== undefined) {
            return maxMarksMap[assessmentType][underscoredField];
        }

        if (maxMarksMap[assessmentType]?.default !== undefined) {
            return maxMarksMap[assessmentType].default;
        }

        const generalFallback = {
            written: 5,
            viva: 5,
            project_presentation: 3,
            project_understanding: 3,
            explanation_technical: 3,
            tools_skill: 4,
        };

        return generalFallback[field] ?? generalFallback[underscoredField] ?? 5;
    };

    const handleMarkChange = (field, value) => {
        const num = Number(value);
        const isValid = num >= 0 && num <= getMaxMark(field);

        setMarkInputs((prev) => ({ ...prev, [field]: value }));
        setMarkErrors((prev) => ({ ...prev, [field]: !isValid }));
    };

    const toSentenceCase = (str) => {
        return str
            .replace(/_/g, ' ')
            .replace(/\w\S*/g, (txt) => {
                return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
            });
    };

    const getAssessmentFields = (assessmentName) => {
        const normalizedName = (assessmentName || '').toLowerCase().trim();
        return Object.keys(maxMarksMap[normalizedName] || {});
    };

    const calculateTotalMarks = (marks, assessmentName) => {
        const fields = getAssessmentFields(assessmentName);
        let obtained = 0;
        let totalMax = 0;

        fields.forEach((field) => {
            const max = getMaxMark(field, assessmentName);
            const score = Number(marks?.[field] ?? 0);
            obtained += score;
            totalMax += max;
        });

        return { obtained, totalMax };
    };

    return (
        <div style={{ padding: '20px', backgroundColor: '#F1F4F9', minHeight: '100vh' }}>
            <ToastContainer
                position="top-center"
                autoClose={2000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />
            <Typography
                variant="h4"
                sx={{
                    color: '#673ab7',
                    marginTop: '10px',
                    marginRight: '30px',
                    fontSize: { xs: '1.5rem', md: '2.125rem' },
                }}
            >
                Student Assessment Entry
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                    {success}
                </Alert>
            )}

            <Card
                sx={{
                    marginTop: '30px',
                    backgroundColor: '#FFFFFF',
                    padding: '20px',
                    borderRadius: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '20px',
                    height: { xs: 'auto', md: '80px' },
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                    flexDirection: { xs: 'column', md: 'row' },
                }}
            >
                <TextField
                    size="small"
                    label="Location"
                    variant="outlined"
                    select
                    value={selectedLocation}
                    disabled={control == 4}
                    onChange={(e) => setSelectedLocation(Number(e.target.value))}
                    sx={{ fontFamily: 'sans-serif', backgroundColor: 'white', width: '20%' }}
                    InputLabelProps={{ shrink: true }}
                >
                    <MenuItem value="0">All Location</MenuItem>
                    <MenuItem value="1">Chennai</MenuItem>
                    <MenuItem value="2">Trichy</MenuItem>
                </TextField>
                <FormControl
                    sx={{
                        flexGrow: 1,
                        width: { xs: '100%', md: '0%' },
                        mb: { xs: 1, md: 0 },
                    }}
                >
                    <Autocomplete
                        id="batch-select"
                        options={batchOptions}
                        getOptionLabel={(option) =>
                            `${option.batch_name} (${new Date(option.start_date).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric',
                            })}) - ${option.course_name}`
                        }
                        renderOption={(props, option) => (
                            <li {...props}>
                                <div>
                                    {option.batch_name} (
                                    {new Date(option.start_date).toLocaleDateString('en-GB', {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric',
                                    })}
                                    )
                                    <br />
                                    <span style={{ fontSize: 'smaller', color: '#666' }}>
                                        {option.course_name}
                                    </span>
                                </div>
                            </li>
                        )}
                        value={selectedBatch}
                        onChange={handleBatchChange}
                        isOptionEqualToValue={(option, value) => option.batch_id === value.batch_id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Batch"
                                sx={{
                                    '& .MuiOutlinedInput-root': { height: '42px' },
                                    '& .MuiInputLabel-root': {
                                        top: '50%',
                                        left: '5%',
                                        transform: 'translateY(-50%)',
                                        '&.MuiInputLabel-shrink': {
                                            top: 0,
                                            transform: 'translate(-28%, -50%)',
                                            backgroundColor: 'white',
                                            paddingLeft: '5px',
                                            paddingRight: '5px',
                                        },
                                    },
                                }}
                            />
                        )}
                    />
                </FormControl>

                <FormControl
                    sx={{
                        flexGrow: 1,
                        width: { xs: '100%', md: '0%' },
                        mb: { xs: 1, md: 0 },
                    }}
                >
                    <Autocomplete
                        id="assessment-select"
                        options={assessmentOptions}
                        getOptionLabel={(option) => option.assesment_name}
                        value={assessmentOptions.find((assessment) => assessment.assesment_name === assessmentValue) || null}
                        onChange={handleAssessmentChange}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Assessment"
                                sx={{
                                    '& .MuiOutlinedInput-root': { height: '42px' },
                                    '& .MuiInputLabel-root': {
                                        top: '50%',
                                        left: '5%',
                                        transform: 'translateY(-50%)',
                                        '&.MuiInputLabel-shrink': {
                                            top: 0,
                                            transform: 'translate(-20%, -50%)',
                                            backgroundColor: 'white',
                                            paddingLeft: '5px',
                                            paddingRight: '5px',
                                        },
                                    },
                                }}
                            />
                        )}
                    />
                </FormControl>

                <TextField
                    id="search-field"
                    label="Search"
                    placeholder="Student name or ID"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{
                        flexGrow: 1,
                        width: { xs: '100%', md: '0%' },
                        mb: { xs: 2, md: 0 },
                        '& .MuiOutlinedInput-root': { height: '42px' },
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: '1.2rem' }} />
                            </InputAdornment>
                        ),
                    }}
                    InputLabelProps={{
                        sx: {
                            top: '50%',
                            left: '5%',
                            transform: 'translateY(-50%)',
                            '&.MuiInputLabel-shrink': {
                                top: 0,
                                transform: 'translate(-27%, -50%)',
                                backgroundColor: 'white',
                                paddingLeft: '5px',
                                paddingRight: '5px',
                            },
                        },
                    }}
                />

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSearchClick}
                    disabled={loading || (!batchValue || !assessmentValue) && !studentsList.length}
                    sx={{
                        height: '42px',
                        width: { xs: '100%', md: '120px' },
                        flexShrink: 0,
                        textTransform: 'uppercase',
                        fontWeight: 'bold',
                        backgroundColor: '#1976d2',
                    }}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'SEARCH'}
                </Button>
            </Card>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && studentsList.length > 0 && (
                <Card
                    sx={{
                        marginTop: '30px',
                        backgroundColor: '#FFFFFF',
                        padding: '20px',
                        borderRadius: '5px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '20px',
                        height: { xs: 'auto', md: 'auto' },
                        flexWrap: { xs: 'wrap', md: 'nowrap' },
                        flexDirection: { xs: 'column', md: 'column' },
                    }}
                >
                    <Box
                        sx={{
                            display: { xs: 'flex', md: 'grid' },
                            flexDirection: { xs: 'column', md: 'unset' },
                            justifyContent: { xs: 'space-between', md: 'unset' },
                            gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' },
                            gap: { xs: 1, md: 2 },
                            alignItems: 'start',
                            width: '100%',
                            maxWidth: '100%',
                        }}
                    >
                        <Box sx={{ width: '100%', maxWidth: '100%', order: { xs: 1, md: 1 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 500,
                                        fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.5rem' },
                                        textTransform: 'uppercase',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: '100%',
                                        textAlign: { xs: 'left', md: 'left' },
                                    }}
                                >
                                    {courseInfo?.batchName || 'BATCH NAME'}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        backgroundColor: '#bbd6ef',
                                        padding: '3px 8px',
                                        border: '1px solid #bbd6ef',
                                        borderRadius: '5px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        '&:before': {
                                            fontSize: { xs: '1rem', md: '1.2rem' },
                                        },
                                    }}
                                >
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#1976d2',
                                            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                                            fontWeight: 600,
                                            textAlign: { xs: 'left', md: 'left' },
                                        }}
                                    >
                                        Student Count: {studentsList.length} {/* Changed from displayedStudents.length */}
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: '#6E7178',
                                    fontSize: { xs: '0.875rem', md: '1rem' },
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '100%',
                                    marginTop: '5px',
                                    textAlign: { xs: 'left', md: 'left' },
                                }}
                            >
                                {courseInfo?.courseName ? `${courseInfo.courseName}` : 'Course name'}
                            </Typography>
                        </Box>

                        <Box sx={{ width: '100%', maxWidth: '100%', order: { xs: 2, md: 2 } }}>
                            <Typography
                                variant="h5"
                                sx={{
                                    color: '#673ab7',
                                    fontWeight: 500,
                                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.5rem' },
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '100%',
                                    textAlign: { xs: 'left', md: 'left' },
                                }}
                            >
                                {selectedAssessmentName || 'DOMAIN TECHNICAL CONCEPT'}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop: '5px' }}>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        fontSize: { xs: '0.875rem', md: '1rem' },
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        maxWidth: '100%',
                                        textAlign: { xs: 'left', md: 'left' },
                                    }}
                                >
                                    {subjectName || 'LV & ELV Technical knowledge'}
                                </Typography>
                                {(control == 1 || control == 2 || control == 4) && (
                                    <IconButton
                                        size="small"
                                        onClick={handleEditSpecificNameClick}
                                        sx={{
                                            color: '#673ab7',
                                            padding: '4px',
                                            '&:hover': {
                                                backgroundColor: 'rgba(103, 58, 183, 0.08)',
                                            },
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' },
                            gap: 2,
                            alignItems: 'start',
                            width: '100%',
                            maxWidth: '100%',
                            overflow: 'hidden',
                        }}
                    >
                        <Box sx={{ width: '100%', maxWidth: '100%', p: 0 }}>
                            <TableContainer
                                component={Card}
                                sx={{
                                    width: '100%',
                                    maxWidth: '100%',
                                    border: '1px solid #EAEAEA',
                                    borderRadius: '4px',
                                    boxShadow: 'none',
                                    m: 0,
                                    overflowX: 'auto',
                                }}
                            >
                                <Table sx={{ minWidth: 0, tableLayout: 'auto' }}>
                                    <TableHead
                                        sx={{
                                            bgcolor: '#DCE3E7',
                                            '& .MuiTableCell-head': {
                                                fontWeight: 600,
                                                color: '#333',
                                            },
                                        }}
                                    >
                                        <TableRow>
                                            <TableCell sx={{ width: '50px' }}>S no</TableCell>
                                            <TableCell>Student name</TableCell>
                                            <TableCell align="center" sx={{ width: '100px' }}>
                                                Status
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedStudents.map((student, index) => (
                                            <TableRow
                                                key={student.student_id}
                                                onClick={() => handleStudentSelect(studentsList.indexOf(student))}
                                                sx={{
                                                    cursor: 'pointer',
                                                    bgcolor:
                                                        selectedRow === studentsList.indexOf(student)
                                                            ? '#B9F4FF'
                                                            : 'white',
                                                    '&:hover': {
                                                        bgcolor:
                                                            selectedRow === studentsList.indexOf(student)
                                                                ? '#B9F4FF'
                                                                : '#f5f7fa',
                                                    },
                                                }}
                                            >
                                                <TableCell
                                                    sx={{ width: '50px', maxWidth: '50px', whiteSpace: 'nowrap' }}
                                                >
                                                    {index + 1 + page * rowsPerPage}
                                                </TableCell>
                                                <TableCell
                                                    sx={{
                                                        maxWidth: { xs: '150px', md: '200px' },
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                                                    >
                                                        {student.student_name}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                                                    >
                                                        {student.userid}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{ width: '100px', maxWidth: '100px' }}
                                                >
                                                    <Chip
                                                        label={
                                                            student.assessments?.find(
                                                                (a) => a.generic_name === activeAssessmentValue
                                                            )?.status || 'pending'
                                                        }
                                                        size="small"
                                                        sx={{
                                                            bgcolor:
                                                                student.assessments?.find(
                                                                    (a) => a.generic_name === activeAssessmentValue
                                                                )?.status === 'completed'
                                                                    ? '#e0f7ed'
                                                                    : '#ffe0e0',
                                                            color:
                                                                student.assessments?.find(
                                                                    (a) => a.generic_name === activeAssessmentValue
                                                                )?.status === 'completed'
                                                                    ? '#00a36f'
                                                                    : '#ff6b6b',
                                                            borderRadius: '4px',
                                                            fontSize: '0.75rem',
                                                            textTransform: 'capitalize',
                                                            maxWidth: '100%',
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                    p: 1,
                                    mt: 1,
                                    gap: 2,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ display: 'flex', alignItems: 'center' }}
                                >
                                    Rows per page:
                                    <Select
                                        value={rowsPerPage}
                                        size="small"
                                        sx={{ ml: 1, minWidth: 60, height: '30px' }}
                                        onChange={handleChangeRowsPerPage}
                                    >
                                        <MenuItem value={5}>5</MenuItem>
                                        <MenuItem value={10}>10</MenuItem>
                                        <MenuItem value={25}>25</MenuItem>
                                    </Select>
                                    <Box sx={{ ml: 2 }}>
                                        {page * rowsPerPage + 1}-
                                        {Math.min((page + 1) * rowsPerPage, displayedStudents.length)} of{' '}
                                        {displayedStudents.length}
                                    </Box>
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <IconButton
                                        size="small"
                                        disabled={page === 0}
                                        onClick={(e) => handleChangePage(e, page - 1)}
                                    >
                                        <ChevronLeftIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        disabled={page >= Math.ceil(displayedStudents.length / rowsPerPage) - 1}
                                        onClick={(e) => handleChangePage(e, page + 1)}
                                    >
                                        <ChevronRightIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                        </Box>

                        <Box sx={{ minWidth: 0, p: 0 }}>
                            <Box sx={{ bgcolor: '#DCE3E7', borderRadius: '8px', p: 2, minHeight: '67%' }}>
                                <Box sx={{ mb: 2, width: '100%' }}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1.5fr 1.5fr 1fr', gap: 2 }}>
                                        <Box sx={{ textAlign: 'left' }}>
                                            <Typography variant="caption" color="text.secondary">
                                                Student name
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                                                    {selectedStudent?.student_name || 'Student name'}
                                                </Typography>
                                                {selectedStudent?.certificate_generated && (
                                                    <MuiTooltip title="Certificate generated, can't able to edit">
                                                        <Chip
                                                            icon={<WorkspacePremiumIcon sx={{ fontSize: '16px !important' }} />}
                                                            label="Certificate Generated"
                                                            size="small"
                                                            sx={{
                                                                bgcolor: '#e0f7ed',
                                                                color: '#00a36f',
                                                                fontWeight: 600,
                                                                fontSize: '0.7rem',
                                                            }}
                                                        />
                                                    </MuiTooltip>
                                                )}
                                            </Box>
                                            <Typography variant="body2" sx={{ color: '#1241E3' }}>
                                                {selectedStudent?.userid || 'QT193289'}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            {/* Removed batch/course text */}
                                        </Box>
                                         <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                                             <Typography
                                                 component="a"
                                                 onClick={isAssessmentCompleted ? moveToNextStudent : undefined}
                                                 sx={{
                                                     color: '#1976d2',
                                                     textDecoration: 'none',
                                                     fontWeight: 500,
                                                     cursor: isAssessmentCompleted ? 'pointer' : 'default',
                                                     opacity: isAssessmentCompleted ? 1 : 0.5,
                                                     pointerEvents: isAssessmentCompleted ? 'auto' : 'none',
                                                     '&:hover': isAssessmentCompleted
                                                         ? { textDecoration: 'underline' }
                                                         : { textDecoration: 'none' },
                                                 }}
                                             >
                                                 View Next Student
                                             </Typography>
                                             {(control == 1 || control == 2 || control == 4) && selectedStudent &&
                                                 !selectedStudent.certificate_generated &&
                                                 Object.values(markInputs).some((val) => val !== '' && val !== null && val !== undefined) && (
                                                 <Button
                                                     variant="outlined"
                                                     color="secondary"
                                                     size="small"
                                                     onClick={() => navigate('/edit-marks', { state: { studentId: selectedStudent.student_id } })}
                                                     sx={{ textTransform: 'none' }}
                                                 >
                                                     Edit Marks
                                                 </Button>
                                             )}
                                         </Box>
                                    </Box>
                                </Box>

                                <Box
                                    sx={{
                                        bgcolor: '#FFFFFF',
                                        borderRadius: '4px',
                                        p: 2,
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                        m: 0,
                                    }}
                                >
                                    <Tabs
                                        value={selectedTab}
                                        onChange={handleTabChange}
                                        sx={{ borderColor: 'divider', mb: 1.5 }}
                                    >
                                        <Tab
                                            icon={<AccessTimeIcon fontSize="small" sx={{ color: '#008000' }} />}
                                            iconPosition="start"
                                            label={activeAssessmentValue || 'DOMAIN TECH...'}
                                            sx={{
                                                minHeight: '36px',
                                                paddingY: '4px',
                                                textTransform: 'uppercase',
                                                fontWeight: selectedTab === 0 ? 600 : 400,
                                                color: selectedTab === 0 ? '#1976d2' : 'text.primary',
                                                '&.Mui-selected': {
                                                    color: '#1976d2',
                                                },
                                            }}
                                        />
                                        <Tab
                                            icon={<AccessTimeIcon fontSize="small" sx={{ color: '#b32e2e' }} />}
                                            iconPosition="start"
                                            label={`PENDING (${pendingAssessments.length})`}
                                            sx={{
                                                minHeight: '36px',
                                                paddingY: '4px',
                                                textTransform: 'uppercase',
                                                fontWeight: selectedTab === 1 ? 600 : 400,
                                                color: selectedTab === 1 ? '#1976d2' : 'text.primary',
                                                '&.Mui-selected': {
                                                    color: '#1976d2',
                                                },
                                            }}
                                        />
                                        <Tab
                                            icon={<CheckCircleIcon fontSize="small" sx={{ color: '#2e6e2e' }} />}
                                            iconPosition="start"
                                            label={`COMPLETED (${completedAssessments.length})`}
                                            sx={{
                                                minHeight: '36px',
                                                paddingY: '4px',
                                                textTransform: 'uppercase',
                                                fontWeight: selectedTab === 2 ? 600 : 400,
                                                color: selectedTab === 2 ? '#1976d2' : 'text.primary',
                                                '&.Mui-selected': {
                                                    color: '#1976d2',
                                                },
                                            }}
                                        />
                                    </Tabs>

                                    {selectedTab === 0 && (
                                        <Box>
                                            {Object.keys(markInputs)
                                                .filter((field) => field !== 'total')
                                                .map((field) => {
                                                    const studentAssessmentKey = `${selectedStudent?.student_id}_${activeAssessmentValue}`;
                                                    const isFieldSubmitted = submittedFields[studentAssessmentKey]?.[field] || false;
                                                    const isFieldDisabled = isAssessmentCompleted || isFieldSubmitted;
                                                    const maxMark = getMaxMark(field);
                                                    const labelText = `${toSentenceCase(field)} (out of ${maxMark})`;

                                                    return (
                                                        <Box key={field} sx={{ mb: 2, mt: 4 }}>
                                                            {isFieldDisabled ? (
                                                                <TextField
                                                                    fullWidth
                                                                    label={labelText}
                                                                    value={markInputs[field] ?? '0'}
                                                                    disabled
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-root': {
                                                                            height: '42px',
                                                                            bgcolor: '#f5f5f5',
                                                                            '&.Mui-disabled': {
                                                                                color: '#000 !important',
                                                                                WebkitTextFillColor: '#000 !important',
                                                                                '& input': {
                                                                                    color: '#000 !important',
                                                                                    WebkitTextFillColor: '#000 !important',
                                                                                },
                                                                            },
                                                                        },
                                                                        '& .MuiInputLabel-root': {
                                                                            fontSize: '1rem',
                                                                            color: 'rgba(0, 0, 0, 0.6)',
                                                                            transform: 'translate(8px, -11px)',
                                                                            background: 'linear-gradient(to top, #f5f5f5 50%, transparent 50%)',
                                                                            padding: '0 8px',
                                                                            maxWidth: '85%',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap',
                                                                            '&.Mui-disabled': {
                                                                                color: 'rgba(0, 0, 0, 0.6) !important',
                                                                            },
                                                                        },
                                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: 'rgba(0, 0, 0, 0.23)',
                                                                            legend: {
                                                                                maxWidth: '100%',
                                                                                width: 'auto',
                                                                                padding: '0 8px',
                                                                            }
                                                                        },
                                                                    }}
                                                                />
                                                            ) : (
                                                                <FormControl fullWidth error={markErrors[field]}>
                                                                    <InputLabel
                                                                        id={`${field}-score-label`}
                                                                        sx={{
                                                                            fontSize: '1rem',
                                                                            background: 'white',
                                                                            paddingX: '5px',
                                                                            position: 'absolute',
                                                                            top: '50%',
                                                                            left: '12px',
                                                                            transform: 'translateY(-50%)',
                                                                            color: 'rgba(0, 0, 0, 0.6)',
                                                                            maxWidth: '85%',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                            whiteSpace: 'nowrap',
                                                                            '&.MuiInputLabel-shrink': {
                                                                                fontSize: '1.2rem',
                                                                                top: 0,
                                                                                left: 0,
                                                                                transform: 'translate(14px, -9px) scale(0.75)',
                                                                                background: 'white',
                                                                                paddingX: '5px',
                                                                            },
                                                                            '&.Mui-focused': {
                                                                                color: 'rgba(0, 0, 0, 0.6)',
                                                                            },
                                                                        }}
                                                                    >
                                                                        {labelText}
                                                                    </InputLabel>
                                                                    <Select
                                                                        labelId={`${field}-score-label`}
                                                                        value={markInputs[field] ?? ''}
                                                                        onChange={(e) => handleMarkChange(field, e.target.value)}
                                                                        displayEmpty
                                                                        renderValue={(val) => (val === '' ? '' : val)}
                                                                        sx={{
                                                                            height: '42px',
                                                                            '& .MuiSelect-select': {
                                                                                height: '42px',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                color: 'black',
                                                                            },
                                                                        }}
                                                                        disabled={false}
                                                                    >
                                                                        <MenuItem value="">
                                                                            <em>Select</em>
                                                                        </MenuItem>
                                                                        {[...Array(getMaxMark(field) + 1).keys()].map((val) => (
                                                                            <MenuItem key={val} value={val.toString()}>
                                                                                {val}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                            )}
                                                        </Box>
                                                    );
                                                })}

                                            <Box sx={{ mb: 2 }}>
                                                <FormControl fullWidth disabled>
                                                    <TextField
                                                        disabled
                                                        id="outlined-disabled"
                                                        label="Total"
                                                        value={
                                                            Object.values(markInputs).every((val) => val !== '' && val !== null && val !== undefined)
                                                                ? Object.values(markInputs).reduce((sum, val) => sum + Number(val), 0)
                                                                : 'N/A'
                                                        }
                                                        defaultValue=""
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                height: '42px',
                                                                bgcolor: '#f5f5f5',
                                                                alignItems: 'center',
                                                                marginTop: '20px',
                                                                '&.Mui-disabled': {
                                                                    color: '#000 !important',
                                                                    WebkitTextFillColor: '#000 !important',
                                                                    '& input': {
                                                                        color: '#000 !important',
                                                                        WebkitTextFillColor: '#000 !important',
                                                                    },
                                                                },
                                                            },
                                                            '& .MuiInputLabel-root': {
                                                                fontSize: '1.3rem',
                                                                color: 'rgba(0, 0, 0, 0.6)',
                                                                transform: 'translate(10px, 9px) scale(0.75)',
                                                                background: 'linear-gradient(to top, #f5f5f5 50%, transparent 50%)',
                                                                padding: '0 5px',
                                                                '&.Mui-disabled': {
                                                                    color: 'rgba(0, 0, 0, 0.6) !important',
                                                                },
                                                            },
                                                        }}
                                                    />
                                                </FormControl>
                                            </Box>

                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSubmitAndContinue();
                                                    }}
                                                    disabled={
                                                        !selectedStudent ||
                                                        loading ||
                                                        isAssessmentCompleted ||
                                                        (allStudentsCompleted() && selectedRow === displayedStudents.length - 1)
                                                    }
                                                    sx={{
                                                        borderRadius: '4px',
                                                        fontWeight: 600,
                                                        px: 3,
                                                        py: 1.5,
                                                        minWidth: '200px',
                                                        backgroundColor: '#1976d2',
                                                        '&:hover': {
                                                            backgroundColor: '#1565c0',
                                                        },
                                                        '&.Mui-disabled': {
                                                            backgroundColor: '#b0bec5',
                                                            color: '#fff',
                                                        },
                                                    }}
                                                >
                                                    {loading ? (
                                                        <CircularProgress size={24} color="inherit" />
                                                    ) : (
                                                        'CONFIRM & CONTINUE'
                                                    )}
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}

                                    {selectedTab === 1 && (
                                        <Box>
                                            {pendingAssessments.length === 0 ? (
                                                <Typography variant="body1" sx={{ textAlign: 'center', py: 3, color: '#666' }}>
                                                    No pending assessments found.
                                                </Typography>
                                            ) : (pendingAssessments.map((item, index) => {
                                                const normalizedName = (item.generic_name || '').toLowerCase().trim();
                                                const assessmentFields = getAssessmentFields(item.generic_name);
                                                // Find the assessment in selectedStudent
                                                const studentAssessment = selectedStudent?.assessments?.find(
                                                    (a) => a.generic_name.toLowerCase().trim() === normalizedName
                                                );
                                                const totalMarks = studentAssessment
                                                    ? calculateTotalMarks(studentAssessment.marks, item.generic_name)
                                                    : { obtained: 0 };
                                                return (
                                                    <Accordion
                                                        key={index}
                                                        sx={{
                                                            mb: 2,
                                                            backgroundColor: '#F9F9F9',
                                                            border: '1px solid #EAEAEA',
                                                            borderRadius: '6px',
                                                            '&:before': { display: 'none' },
                                                        }}
                                                    >
                                                        <AccordionSummary
                                                            expandIcon={<ExpandMoreIcon />}
                                                            sx={{
                                                                px: 3,
                                                                py: 1,
                                                                '& .MuiAccordionSummary-content': {
                                                                    display: 'flex',
                                                                    justifyContent: 'space-between',
                                                                    alignItems: 'center',
                                                                },
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                    <Box>
                                                                        <Typography variant="h6" sx={{ fontWeight: 500, color: '#333' }}>
                                                                            {item.generic_name || 'Assessment Name Missing'}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                                            {item.specific_name || 'N/A'}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box
                                                                        sx={{
                                                                            bgcolor: '#ffe0e0',
                                                                            color: '#ff6b6b',
                                                                            borderRadius: '12px',
                                                                            px: 2,
                                                                            py: 0.5,
                                                                            fontWeight: 600,
                                                                            fontSize: '0.85rem',
                                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                                            width: 'fit-content',
                                                                        }}
                                                                    >
                                                                        Total Marks: {totalMarks.obtained ? `${totalMarks.obtained}` : 'N/A'}
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        </AccordionSummary>
                                                        <AccordionDetails sx={{ px: 3, py: 2 }}>
                                                            <Box sx={{ ml: '20px', mr: '20px' }}>
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        justifyContent: 'space-around',
                                                                        flexWrap: 'wrap',
                                                                        gap: { xs: 2, md: 6 },
                                                                        mt: 2,
                                                                        width: '100%',
                                                                    }}
                                                                >
                                                                    {assessmentFields.map((field) => {
                                                                        const total = getMaxMark(field, item.generic_name);
                                                                        return (
                                                                            <DoughnutChart
                                                                                key={field}
                                                                                obtained={0}
                                                                                total={total}
                                                                                label={toSentenceCase(field)}
                                                                                sx={{ minWidth: '120px', maxWidth: '120px' }}
                                                                                disabled={true}
                                                                                displayText={`N/A / ${total}`}
                                                                            />
                                                                        );
                                                                    })}
                                                                </Box>
                                                            </Box>
                                                        </AccordionDetails>
                                                    </Accordion>
                                                );
                                            })
                                            )}
                                        </Box>
                                    )}

                                    {selectedTab === 2 && (
                                        <Box>
                                            {completedAssessments.length === 0 ? (
                                                <Typography
                                                    variant="body1"
                                                    sx={{ textAlign: 'center', py: 3, color: '#666' }}
                                                >
                                                    No completed assessments found.
                                                </Typography>
                                            ) : (
                                                completedAssessments.map((item, index) => {
                                                    const normalizedName = (item.generic_name || '').toLowerCase().trim();
                                                    const assessmentFields = getAssessmentFields(item.generic_name);
                                                    const totalMarks = calculateTotalMarks(item.marks, item.generic_name);
                                                    return (
                                                        <Accordion
                                                            key={index}
                                                            sx={{
                                                                mb: 2,
                                                                backgroundColor: '#F9F9F9',
                                                                border: '1px solid #EAEAEA',
                                                                borderRadius: '6px',
                                                                '&:before': { display: 'none' },
                                                            }}
                                                        >
                                                            <AccordionSummary
                                                                expandIcon={<ExpandMoreIcon />}
                                                                sx={{
                                                                    px: 3,
                                                                    py: 1,
                                                                    '& .MuiAccordionSummary-content': {
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center',
                                                                    },
                                                                }}
                                                            >
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                        <Box>
                                                                            <Typography variant="h6" sx={{ fontWeight: 500, color: '#333' }}>
                                                                                {item.generic_name || 'Assessment Name Missing'}
                                                                            </Typography>
                                                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                                                {item.specific_name || 'N/A'}
                                                                            </Typography>
                                                                        </Box>
                                                                        <Box
                                                                            sx={{
                                                                                bgcolor: '#e0f7ed',
                                                                                color: '#00a36f',
                                                                                borderRadius: '12px',
                                                                                px: 2,
                                                                                py: 0.5,
                                                                                fontWeight: 600,
                                                                                fontSize: '0.85rem',
                                                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                                                                width: 'fit-content',
                                                                            }}
                                                                        >
                                                                            Total Marks: {totalMarks.obtained ? `${totalMarks.obtained}` : 'N/A'}
                                                                        </Box>
                                                                    </Box>
                                                                </Box>
                                                            </AccordionSummary>
                                                            <AccordionDetails sx={{ px: 3, py: 2 }}>
                                                                <Box sx={{ ml: '20px', mr: '20px' }}>
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            justifyContent: 'space-around',
                                                                            flexWrap: 'wrap',
                                                                            gap: { xs: 2, md: 6 },
                                                                            mt: 2,
                                                                            width: '100%',
                                                                        }}
                                                                    >
                                                                        {assessmentFields.map((field) => {
                                                                            const obtained = Number(item.marks?.[field] ?? 0);
                                                                            const total = getMaxMark(field, item.generic_name);
                                                                            return (
                                                                                <DoughnutChart
                                                                                    key={field}
                                                                                    obtained={obtained}
                                                                                    total={total}
                                                                                    label={toSentenceCase(field)}
                                                                                    sx={{ minWidth: '120px', maxWidth: '120px' }}
                                                                                />
                                                                            );
                                                                        })}
                                                                    </Box>
                                                                </Box>
                                                            </AccordionDetails>
                                                        </Accordion>
                                                    );
                                                })
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Card>
            )}

            <Dialog
                open={isEditDialogOpen}
                onClose={() => !isSavingName && setIsEditDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle sx={{ fontWeight: 600 }}>Edit Specific Name</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Specific Name"
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={editSpecificNameInput}
                        onChange={(e) => setEditSpecificNameInput(e.target.value)}
                        disabled={isSavingName}
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setIsEditDialogOpen(false)}
                        disabled={isSavingName}
                        variant="outlined"
                        color="secondary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveSpecificNameClick}
                        disabled={isSavingName || !editSpecificNameInput.trim()}
                        variant="contained"
                        color="primary"
                    >
                        {isSavingName ? <CircularProgress size={24} color="inherit" /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={isConfirmNameDialogOpen}
                onClose={() => !isSavingName && setIsConfirmNameDialogOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle
                    sx={{
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: '#FFF4E5',
                        color: '#8A5300',
                    }}
                >
                    <WarningAmberIcon sx={{ color: '#ED6C02' }} />
                    Confirm Specific Name
                    <IconButton
                        onClick={() => setIsConfirmNameDialogOpen(false)}
                        disabled={isSavingName}
                        size="small"
                        sx={{ ml: 'auto', color: '#8A5300' }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', alignItems: 'center', gap: 2, pt: 3 }}>
                    <WarningAmberIcon sx={{ color: '#ED6C02', fontSize: '2.5rem' }} />
                    <Typography>
                        This name — "{editSpecificNameInput.trim()}" — will be printed on the student's certificate. Are you okay with it?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setIsConfirmNameDialogOpen(false)}
                        disabled={isSavingName}
                        variant="outlined"
                        color="secondary"
                    >
                        No
                    </Button>
                    <Button
                        onClick={handleSaveSpecificName}
                        disabled={isSavingName}
                        variant="contained"
                        sx={{ bgcolor: '#ED6C02', '&:hover': { bgcolor: '#C55A02' } }}
                    >
                        {isSavingName ? <CircularProgress size={24} color="inherit" /> : 'Yes, Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default StudentAssessmentEntryForm;
